import { useEffect, useCallback, useRef } from "react";
import { mainBus, metricsBus } from "@/common/buses";
import { NavigationType, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { createFullscreenEntry, getFullscreenEntry, getPageKey } from "@/common/utils/fullscreenHistory";
import { useAppStore } from "@/common/store/appStore";
import { useBodyLock } from "@/common/hooks/useBodyLock";
import { useScrollStore } from "@/common/store/scrollStore";
import { useVideoQueueStore } from "@/common/store/videoQueueStore";
import { useBoostedGifStore } from "@/common/store/boostedGifStore";
import { MOBILE_FULLSCREEN_VARIANT_C } from "@/common/services/experiment/constants";
import { useMobileFullscreenVariant } from "@/common/hooks/useMobileFullscreenVariant";

type CheckableVisibilityElement = HTMLElement & {
  checkVisibility?: () => boolean;
};

const fallbackIsElementVisible = (element: HTMLElement) => {
  const styles = window.getComputedStyle(element);
  return styles.display !== "none" && styles.visibility !== "hidden";
};

const isElementVisible = (element: CheckableVisibilityElement) => {
  const visibilityFn = element.checkVisibility;

  if (typeof visibilityFn === "function") {
    try {
      return visibilityFn.call(element);
    } catch {
      return fallbackIsElementVisible(element);
    }
  }

  return fallbackIsElementVisible(element);
};

export const useFullScreen = () => {
  const isFullScreen = useAppStore((state) => state.isFullScreen);
  const isMobileFullscreen = useAppStore((state) => state.isMobileFullscreen);
  const toggleFullScreen = useAppStore((state) => state.toggleFullScreen);
  const toggleMobileFullscreen = useAppStore((state) => state.toggleMobileFullscreen);
  const scrollElement = useScrollStore((state) => state.scrollElement);
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const getLastVisibleVideo = useVideoQueueStore((state) => state.getLastVisibleVideo);
  const videoQueue = useVideoQueueStore((s) => s.videoQueue);
  const boostedGifsData = useBoostedGifStore((state) => state.gifs);

  const fallbackFeedItemRef = useRef<string | null>(null);
  const enteredGifRef = useRef<string | null>(null);
  const sessionRef = useRef<{ entryKey: string; pageKey: string; sourceKey: string } | null>(null);
  const openingRef = useRef<{ pageKey: string } | null>(null);
  const alignmentRef = useRef<(() => void) | null>(null);
  const pageKeyRef = useRef(getPageKey(location));
  const locationKeyRef = useRef(location.key);
  const closingRef = useRef(false);
  const exitRef = useRef<{ feedItem?: string; align: boolean } | null>(null);

  useEffect(() => {
    if (!isFullScreen) {
      return;
    }

    const lastKnownGif = getLastVisibleVideo();

    if (typeof lastKnownGif !== "string") {
      return;
    }

    if (boostedGifsData.find(gif => gif.id === lastKnownGif)) {
      return;
    }

    fallbackFeedItemRef.current = lastKnownGif;
  }, [videoQueue, isFullScreen, boostedGifsData]);

  const findFeedItemElement = useCallback((feedItemId: string | null) => {
    if (!feedItemId) {
      return null;
    }

    const selector = `[data-feed-item-id="${feedItemId}"]`;

    for (const element of document.querySelectorAll<CheckableVisibilityElement>(selector)) {
      if (isElementVisible(element)) {
        return element;
      }
    }

    return null;
  }, []);

  const getActiveFeedItemId = useCallback(() => {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const elementAtCenter = document.elementFromPoint(centerX, centerY);

    if (!elementAtCenter || typeof elementAtCenter.closest !== "function") {
      return undefined;
    }

    const moduleElement = elementAtCenter.closest("[data-feed-item-id]") as HTMLElement;

    if (!moduleElement) {
      return undefined;
    }

    return moduleElement.dataset.feedItemId ?? undefined;
  }, []);

  const cancelAlignment = useCallback(() => {
    alignmentRef.current?.();
    alignmentRef.current = null;
  }, []);

  const scrollIntoView = useCallback((feedItem?: string) => {
    cancelAlignment();

    if (!feedItem) {
      scrollElement?.scrollTo({ top: 0 });
      return;
    }

    const requestedPageKey = pageKeyRef.current;

    const performScroll = () => {
      alignmentRef.current = null;

      if (pageKeyRef.current !== requestedPageKey) {
        return;
      }

      const element = findFeedItemElement(feedItem);

      if (element) {
        element.scrollIntoView({ behavior: "auto", block: "center" });
        return;
      }

      if (fallbackFeedItemRef.current) {
        const fallbackElement = findFeedItemElement(fallbackFeedItemRef.current);

        if (fallbackElement) {
          fallbackElement.scrollIntoView({ behavior: "auto", block: "center" });
          return;
        }
      }

      scrollElement?.scrollTo({ top: 0 });
    };

    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      const frame = window.requestAnimationFrame(performScroll);
      alignmentRef.current = () => window.cancelAnimationFrame(frame);
      return;
    }

    const timeout = window.setTimeout(performScroll, 0);
    alignmentRef.current = () => window.clearTimeout(timeout);
  }, [findFeedItemElement, scrollElement, cancelAlignment]);

  useEffect(() => {
    const pageKey = getPageKey(location);
    const leftThePage = pageKeyRef.current !== pageKey;
    const movedToAnotherRecord = locationKeyRef.current !== location.key && !openingRef.current;

    if (leftThePage || movedToAnotherRecord) {
      cancelAlignment();
    }

    pageKeyRef.current = pageKey;
    locationKeyRef.current = location.key;
  }, [location, cancelAlignment]);

  useEffect(() => cancelAlignment, [cancelAlignment]);

  useEffect(() => {
    if (!isFullScreen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const parent = document.querySelector(".previewFeed");

      if (!parent) {
        return;
      }

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          mainBus.emit("toggle_fullscreen");
          break;

        case "ArrowDown":
          event.preventDefault();
          parent.scrollBy({
            top: window.innerHeight,
            behavior: "smooth",
          });
          break;

        case "ArrowUp":
          event.preventDefault();
          parent.scrollBy({
            top: -window.innerHeight,
            behavior: "smooth",
          });
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [findFeedItemElement, isFullScreen]);

  const openSession = useCallback(() => {
    if (openingRef.current ?? sessionRef.current) {
      return;
    }

    const pageKey = getPageKey(location);

    openingRef.current = { pageKey };
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, {
      state: {
        ...(location.state as Record<string, unknown> | null),
        gifFullscreen: createFullscreenEntry(pageKey, location.key),
      },
    });
  }, [location, navigate]);

  const closeSession = useCallback((feedItem?: string, align = true) => {
    exitRef.current = { feedItem, align };

    if (sessionRef.current && !closingRef.current) {
      closingRef.current = true;
      navigate(-1);
      return;
    }

    exitRef.current = null;
    enteredGifRef.current = null;
    toggleFullScreen(false);
    toggleMobileFullscreen(false);

    if (align) {
      scrollIntoView(feedItem);
    }
  }, [navigate, toggleFullScreen, toggleMobileFullscreen, scrollIntoView]);

  useEffect(() => {
    const session = sessionRef.current;
    const opening = openingRef.current;
    const currentEntry = getFullscreenEntry(location);

    if (opening) {
      if (currentEntry && currentEntry.pageKey === opening.pageKey) {
        openingRef.current = null;
        sessionRef.current = {
          entryKey: location.key, pageKey: currentEntry.pageKey, sourceKey: currentEntry.sourceKey,
        };
        return;
      }

      if (getPageKey(location) === opening.pageKey) {
        return;
      }

      openingRef.current = null;
      closingRef.current = false;
      exitRef.current = null;
      enteredGifRef.current = null;
      toggleFullScreen(false);
      toggleMobileFullscreen(false);
      return;
    }

    if (!session || session.entryKey === location.key) {
      return;
    }

    if (navigationType === NavigationType.Replace && currentEntry && currentEntry.pageKey === session.pageKey) {
      const { scrolls, setScroll } = useScrollStore.getState();
      const carried = scrolls[session.entryKey];

      if (carried) {
        setScroll(location.key, carried);
      }

      sessionRef.current = {
        entryKey: location.key, pageKey: session.pageKey, sourceKey: currentEntry.sourceKey,
      };
      return;
    }

    const exit = exitRef.current;
    sessionRef.current = null;
    openingRef.current = null;
    closingRef.current = false;
    exitRef.current = null;
    enteredGifRef.current = null;

    const { scrolls, setScroll } = useScrollStore.getState();
    const sessionScroll = scrolls[session.entryKey];

    if (sessionScroll) {
      setScroll(session.sourceKey, sessionScroll);
    }

    toggleFullScreen(false);
    toggleMobileFullscreen(false);

    if (location.key === session.sourceKey && (!exit || exit.align)) {
      scrollIntoView(exit?.feedItem ?? getActiveFeedItemId());
    }
  }, [location, navigationType, toggleFullScreen, toggleMobileFullscreen, scrollIntoView, getActiveFeedItemId]);

  useEffect(() => () => {
    toggleFullScreen(false);
    toggleMobileFullscreen(false);
  }, [toggleFullScreen, toggleMobileFullscreen]);

  const toggleFullscreen = useCallback((payload?: { id?: string }) => {
    const targetId = payload?.id ?? getActiveFeedItemId();

    if (isFullScreen) {
      closeSession(targetId);
      return;
    }

    toggleFullScreen(true);
    scrollIntoView(targetId);
    openSession();
  }, [
    isFullScreen,
    toggleFullScreen,
    scrollIntoView,
    getActiveFeedItemId,
    openSession,
    closeSession
  ]);

  useEffect(() => {
    mainBus.on("toggle_fullscreen", toggleFullscreen);

    return () => {
      mainBus.off("toggle_fullscreen", toggleFullscreen);
    };
  }, [toggleFullscreen]);

  const mobileFullscreenVariant = useMobileFullscreenVariant(isMobileFullscreen);

  const toggleMobileFullscreenHandler = useCallback(({ id }: { id: string }) => {
    if (isMobileFullscreen) {
      metricsBus.emit("forceFullscreenExited");

      if (isFullScreen) {
        enteredGifRef.current = null;
        toggleMobileFullscreen(false);
        scrollIntoView(id);
        return;
      }

      closeSession(id);
      return;
    }

    enteredGifRef.current = id;
    toggleMobileFullscreen(true);
    metricsBus.emit("forceFullscreenEntered");
    scrollIntoView(id);
    openSession();
  }, [
    isMobileFullscreen,
    isFullScreen,
    toggleMobileFullscreen,
    scrollIntoView,
    openSession,
    closeSession
  ]);

  useEffect(() => {
    mainBus.on("toggle_mobile_fullscreen", toggleMobileFullscreenHandler);

    return () => {
      mainBus.off("toggle_mobile_fullscreen", toggleMobileFullscreenHandler);
    };
  }, [toggleMobileFullscreenHandler]);

  /* Variant C: exiting mobile fullscreen when leaving the entered GIF */
  useEffect(() => {
    if (
      !isMobileFullscreen ||
      mobileFullscreenVariant !== MOBILE_FULLSCREEN_VARIANT_C ||
      !enteredGifRef.current
    ) {
      return;
    }

    if (getLastVisibleVideo() === enteredGifRef.current) {
      return;
    }

    enteredGifRef.current = null;

    if (isFullScreen) {
      toggleMobileFullscreen(false);
      return;
    }

    closeSession(undefined, false);
  }, [
    videoQueue,
    isFullScreen,
    isMobileFullscreen,
    mobileFullscreenVariant,
    toggleMobileFullscreen,
    getLastVisibleVideo,
    closeSession
  ]);

  useBodyLock(isFullScreen);
};
